import { MatDialog } from "@angular/material/dialog";
import { Observable } from "rxjs";
import { take } from "rxjs/operators";
import { CardModel } from "../../../swagger/api.client.generated";
import {
  DialogEditCardComponent,
  EditCardDeckEntry,
  EditCardDialogData,
  EditCardDialogMode,
  EditCardDialogResult,
} from "../dialog-edit-card/dialog-edit-card.component";
import { isBlankCardName } from "./card-type.utils";
import { SafetyCardCategory } from "./card.models";

// tslint:disable-next-line:max-line-length
const URL_REGEX = /\b((?:[a-z][\w-]+:(?:\/{1,3}|[a-z0-9%])|www\d{0,3}[.]|[a-z0-9.\-]+[.][a-z]{2,4}\/)(?:[^\s()<>]+|\(([^\s()<>]+|(\([^\s()<>]+\)))*\))+(?:\(([^\s()<>]+|(\([^\s()<>]+\)))*\)|[^\s`!()\[\]{};:'".,<>?«»""'']))/g;
const AUDIO_REGEX = /^(https?|ftp|file):\/\/(www.)?(.*?)\.(mp3|wav)$/gm;

export interface CardContentResult {
  isMediaCard: boolean;
  mediaIcon: string;
  cardHtml: string;
}

export function processCardContent(
  cardName: string,
  _htmlScript?: string | null
): CardContentResult {
  const name = cardName || "";
  const urls = name.match(URL_REGEX);

  if (urls && urls.length > 0) {
    const firstUrl = urls[0];
    const title = name.replace(firstUrl, "").trim();

    if (firstUrl.includes("youtube.com") || firstUrl.includes("youtu.be/")) {
      return {
        isMediaCard: true,
        mediaIcon: "fas fa-video",
        cardHtml: `<div class="media-title">Watch ${title || "Video"}</div>`,
      };
    }

    if (firstUrl.match(AUDIO_REGEX)) {
      return {
        isMediaCard: true,
        mediaIcon: "fas fa-podcast",
        cardHtml: `<div class="media-title">Listen to ${title || "Audio"}</div>`,
      };
    }

    return { isMediaCard: false, mediaIcon: "fas fa-film", cardHtml: title ? `<div>${title}</div>` : "" };
  }

  return {
    isMediaCard: false,
    mediaIcon: "fas fa-film",
    cardHtml: isBlankCardName(name) ? "" : `<div>${name}</div>`,
  };
}

/**
 * Opens the edit card dialog and emits the result once (or null on dismiss).
 * `mode` selects the action (Edit forces a radio choice, Duplicate pre-selects + suffixes the name);
 * `deck` is the patient's deck for the live duplicate-name check; `isMandatory` locks the name.
 */
export function openEditCardDialog(
  dialog: MatDialog,
  cardModel: CardModel,
  mode: EditCardDialogMode = "edit",
  deck: CardModel[] = [],
  isMandatory: boolean = false,
  options?: {
    cardKind: "safety" | "activity";
    safetyCategory?: SafetyCardCategory | null;
    isCreate?: boolean;
  }
): Observable<EditCardDialogResult | null> {
  const deckCards: EditCardDeckEntry[] = (deck ?? []).map((c) => ({
    cardId: c.cardId ?? 0,
    cardGuid: c.cardGuid,
    cardName: c.cardName ?? "",
    periodMinutes: c.periodMinutes ?? 0,
  }));
  const data: EditCardDialogData = {
    card: cardModel,
    cardName: cardModel.cardName ?? "",
    mode,
    deckCards,
    isMandatory,
    cardKind: options?.cardKind ?? "safety",
    safetyCategory: options?.safetyCategory ?? null,
    title: options?.isCreate ? "Create Your Card" : undefined,
  };
  return dialog.open(DialogEditCardComponent, {
    data,
  }).afterClosed().pipe(take(1));
}

/** Mutates cardModel in-place after a successful edit, returns the new cardHtml. */
export function applyCardEditResult(
  cardModel: CardModel,
  result: { cardName: string; periodMinutes?: number; description?: string }
): string {
  cardModel.cardName = result.cardName;
  cardModel.periodMinutes = result.periodMinutes ?? 0;
  // Carry the back text so the flip icon (*ngIf="cardDescription && showFlipIcon") reflects
  // it immediately on create/edit/duplicate. Clearing the text (undefined) removes the icon.
  cardModel.cardDescription = result.description ?? undefined;
  cardModel.isPatientDeck = true;
  return processCardContent(result.cardName).cardHtml;
}
